import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import HostedCardPanel, { warmHostedSession } from '../payments/HostedCardPanel';
import SavedCardPicker from '../ClientWallet/SavedCardPicker';
import SavedCardPay from '../payments/SavedCardPay';
import PayNowPanel from '../PayNowPanel';
import '../ClientWallet/savedCards.css';
import '../publicInvoicePay.css';
import './accountPay.css';

/**
 * The permanent pay page — /pay/account/<token>.
 * Spec, and the reasoning behind what it shows: PLAN_payment_links.md
 *
 * WEARS THE SAME CLOTHES AS /pay/<token>. Both are opened by somebody who may
 * have nothing but the link — no account, no app, no email thread — so both
 * carry the same brand block, the same contact footer and the same card. The
 * shared stylesheet is imported rather than reimplemented so the two cannot
 * drift into looking like different companies.
 *
 * THE LINK NEVER CARRIES AN AMOUNT. It names a client, and every open reads
 * the live pending position — so a reminder sent in October is still right in
 * March, and somebody who paid yesterday sees a settled account.
 *
 * WHAT IS VISIBLE WITHOUT SIGNING IN: invoice numbers, dates, amounts, and a
 * pay button. No properties, no unit numbers, no line items — those live in
 * the PDFs and the statement, behind a session.
 */
export default function PublicAccountPay() {
    const { token } = useParams();
    const [params] = useSearchParams();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [paying, setPaying] = useState(false);
    const [notice, setNotice] = useState('');

    /* EMBEDDED CARD (the same rail /pay/<token> uses). The payer types their
       card on this page and 3DS runs in an overlay on it — no redirect. The
       panel downgrades itself to the redirect button if the SDK cannot mount,
       because somebody sent a payment link must never meet a broken form.

       'probe' until the panel reports; 'off' means fall back to the paypage. */
    const [hostedRail, setHostedRail] = useState('probe');
    const [step, setStep] = useState('choose');
    const [paidHere, setPaidHere] = useState(false);

    /* SIGNED-IN OWNER RAILS (2026-09-02, owner request): the permanent link
       grows the full client checkout when the viewer IS this client — a
       wallet toggle (split with the card when the balance doesn't cover),
       saved cards, save-this-card. Anonymous holders see the page unchanged
       and are never nagged to log in for it. */
    const [useWallet, setUseWallet] = useState(true);
    const [cardSelection, setCardSelection] = useState('new');
    const [savedCvv, setSavedCvv] = useState('');
    const [savedCardBusy, setSavedCardBusy] = useState(false);
    const [saveNewCard, setSaveNewCard] = useState(false);
    const [walletPaying, setWalletPaying] = useState(false);
    // Separate entity wallets: which pool this page is paying (null = the
    // main account). An entity with its own wallet is paid on its own.
    const [poolId, setPoolId] = useState(null);
    const walletPortionRef = useRef(0);
    const poolIdRef = useRef(null);
    const saveNewCardRef = useRef(false);
    // The panel's Pay button is portalled into the Back/Pay row so the step
    // ends in one row rather than two stacked bars.
    const [paySlot, setPaySlot] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`/api/account-link/${token}/summary`);
            setData(res.data);
            const cards = Array.isArray(res.data?.cards) ? res.data.cards : [];
            // Default to the newest saved card, once, like every client checkout.
            setCardSelection((prev) => (prev === 'new' && cards.length > 0 ? cards[0].id : prev));
            setError('');
        } catch (e) {
            setError(e.response?.status === 404
                ? 'This payment link is not valid. Please ask Holiday Home Cleaning for a new one.'
                : 'Could not load your account. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    /* Open the gateway connection and fetch the SDK while they are still
       reading the amount. This page exists to be opened from a WhatsApp
       reminder on a phone, where the card form's three serial round trips were
       all being paid for after the tap (2026-08-29). */
    useEffect(() => {
        warmHostedSession(`/api/account-link/${token}/hosted-session/config`);
    }, [token]);

    /* Back from the gateway: settle through the same confirmCheckout every
       other rail uses, then re-read so the page shows the new position. */
    useEffect(() => {
        const ref = params.get('ref');
        if (params.get('from') !== 'gateway' || !ref) return;
        (async () => {
            setNotice('Confirming your payment…');
            try {
                await axios.post(`/api/account-link/${token}/verify`, { order_ref: ref });
                setNotice('');
            } catch {
                setNotice('We could not confirm that automatically. If you were charged it will appear here shortly.');
            }
            await load();
        })();
    }, [params, token, load]);

    const pay = async () => {
        if (paying) return;
        setPaying(true);
        setError('');
        try {
            const res = await axios.post(`/api/account-link/${token}/checkout`, { ...(poolId ? { wallet_client_id: poolId } : {}) });
            if (res.data?.nothing_due) { await load(); return; }
            if (res.data?.payment_url) { window.location.href = res.data.payment_url; return; }
            setError('Could not start the payment. Please try again.');
        } catch (e) {
            setError(e.response?.data?.error || 'Could not start the payment. Please try again.');
        } finally {
            setPaying(false);
        }
    };

    /* Start an EMBEDDED payment: the same checkout endpoint, handed the SDK's
       session id, returning the gateway payment object for 3DS. */
    const startHostedPayment = useCallback(async (sessionId) => {
        setError('');
        try {
            const res = await axios.post(`/api/account-link/${token}/checkout`, {
                session_id: sessionId,
                ...(poolIdRef.current ? { wallet_client_id: poolIdRef.current } : {}),
                wallet_portion: walletPortionRef.current,
                ...(saveNewCardRef.current ? { save_card: true, save_card_consent: true } : {}),
            });
            if (res.data?.nothing_due) { await load(); return null; }
            if (!res.data?.payment_response) {
                setError('Could not start the payment. Please try again.');
                return null;
            }
            return res.data;
        } catch (e) {
            setError(e.response?.data?.error || 'Could not start the payment. Please try again.');
            // The commonest reason a checkout is refused is that the debt
            // closed while this page was open — paid another way, or recorded
            // in Zoho by hand. Re-reading turns the form into the settled view
            // rather than leaving an error over an amount that is no longer due.
            load();
            return null;
        }
    }, [token, load]);

    const verifyHostedPayment = useCallback(
        (orderRef) => axios.post(`/api/account-link/${token}/verify`, { order_ref: orderRef }).then((r) => r.data),
        [token]
    );

    const money = (n) => Number(n || 0).toLocaleString('en-AE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    });

    /* "17 Aug 2026", not "2026-08-17" — this page is read by a person, not
       parsed by one. Falls back to whatever came if it will not parse. */
    const when = (d) => {
        if (!d) return '';
        const t = new Date(d);
        return Number.isNaN(t.getTime())
            ? String(d)
            : t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const pools = Array.isArray(data?.pools) && data.pools.length > 1 ? data.pools : [];
    const pool = pools.find((p) => (p.wallet_client_id || null) === poolId) || pools[0] || null;
    const invoices = (data?.invoices || []).filter((i) => !pools.length || (i.wallet_client_id || null) === (pool?.wallet_client_id || null));
    const total = pools.length ? Number(pool?.total || 0) : Number(data?.total_outstanding || 0);
    const allSettled = data && Number(data?.total_outstanding || 0) <= 0.009;
    const settled = data && (allSettled || paidHere);

    // The owner's split arithmetic — display-only; the server re-clamps.
    const authed = !!data?.authenticated;
    const savedCards = authed && Array.isArray(data?.cards) ? data.cards : [];
    const walletCeiling = authed ? Number((pools.length ? pool?.wallet : data?.wallet)?.ceiling || 0) : 0;
    const walletOn = authed && useWallet && walletCeiling > 0.005;
    const walletPortion = walletOn ? Math.min(walletCeiling, total) : 0;
    const cardPortion = Math.max(0, Math.round((total - walletPortion) * 100) / 100);
    const walletCovers = walletOn && cardPortion <= 0.009 && walletPortion > 0.009;
    const selectedSavedCard = savedCards.find((c) => c.id === cardSelection) || null;
    const savedCardActive = authed && typeof cardSelection === 'number' && !!selectedSavedCard;
    // Refs, because startHostedPayment is declared above this derivation —
    // the panel reads the CURRENT split at pay time, never a stale closure.
    walletPortionRef.current = walletPortion;
    saveNewCardRef.current = saveNewCard;
    poolIdRef.current = poolId;

    const payFromWallet = async () => {
        if (walletPaying) return;
        setWalletPaying(true);
        setError('');
        try {
            const res = await axios.post(`/api/account-link/${token}/checkout`, { wallet_portion: total, ...(poolId ? { wallet_client_id: poolId } : {}) });
            if (res.data?.nothing_due || (res.data?.ok && res.data?.rail === 'wallet')) {
                setPaidHere(true);
                await load();
            } else {
                setError('Could not complete the wallet payment. Please try again.');
            }
        } catch (e) {
            setError(e.response?.data?.error || 'Could not complete the wallet payment. Please try again.');
            load();
        } finally {
            setWalletPaying(false);
        }
    };

    const chargeSavedCard = useCallback(
        (cvv) => axios.post(`/api/account-link/${token}/checkout`, {
            payment_method_id: cardSelection,
            cvv,
            wallet_portion: walletPortion,
            ...(poolId ? { wallet_client_id: poolId } : {}),
        }).then((r) => r.data),
        [token, cardSelection, walletPortion, poolId]
    );

    return (
        <div className="pubpay">
            <div className="pubpay__card">
                <header className="pubpay__brandBlock">
                    <a
                        className="pubpay__logoLink"
                        href="https://holidayhomecleaning.ae"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Holiday Home Cleaning website"
                    >
                        <img className="pubpay__logo" src="/logo.png" alt="Holiday Home Cleaning" />
                    </a>
                    <div className="pubpay__brand">Holiday Home Cleaning Services Co. L.L.C</div>
                </header>

                {/* A SHAPE, NOT A WORD. The reader is about to be shown a figure
                    they may owe; a skeleton of the thing that is coming reads as
                    the page working, where "Loading…" reads as a page that might
                    not. */}
                {loading && (
                    <div className="apay__skeleton" aria-busy="true" aria-label="Loading your account">
                        <div className="apay__sk apay__sk--label" />
                        <div className="apay__sk apay__sk--total" />
                        <div className="apay__sk apay__sk--row" />
                        <div className="apay__sk apay__sk--row" />
                        <div className="apay__sk apay__sk--btn" />
                    </div>
                )}

                {!loading && error && !data && <div className="pubpay__error">{error}</div>}

                {!loading && data && (
                    <>
                        <div className="pubpay__identity">
                            <div className="pubpay__invoiceLabel">Account</div>
                            <div className="pubpay__invoiceNumber">{data.client_name}</div>
                        </div>

                        {settled ? (
                            /* NOTHING OWED IS THE ANSWER, not an empty state. The
                               commonest reason to open a reminder link is to
                               check, and "you are all paid up" is what they came
                               for. */
                            <div className="apay__clear">
                                <div className="apay__clearMark">✓</div>
                                <h2>You’re all paid up</h2>
                                <p>There is nothing outstanding on your account right now. Thank you.</p>
                            </div>
                        ) : (
                            <>
                                {pools.length > 0 && (
                                    <div className="apay__pools" role="tablist" aria-label="Accounts">
                                        {pools.map((p) => {
                                            const on = (p.wallet_client_id || null) === (pool?.wallet_client_id || null);
                                            return (
                                                <button
                                                    key={p.wallet_client_id || 'main'}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={on}
                                                    className={`apay__pool${on ? ' is-on' : ''}`}
                                                    onClick={() => { setPoolId(p.wallet_client_id || null); setStep('choose'); }}
                                                >
                                                    <span>{p.label || data?.client_name || 'Main account'}</span>
                                                    <strong>AED {money(Number(p.total || 0))}</strong>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {pools.length > 0 && total <= 0.009 ? (
                                    <div className="apay__totalBlock">
                                        <div className="apay__label">Nothing outstanding for {pool?.label || 'this account'}</div>
                                    </div>
                                ) : (
                                <div className="apay__totalBlock">
                                    <div className="apay__label">{pools.length ? `Outstanding — ${pool?.label || data?.client_name || 'Main account'}` : 'Total outstanding'}</div>
                                    <div className="apay__total">AED {money(total)}</div>
                                    <div className="apay__count">
                                        across {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
                                    </div>
                                </div>
                                )}

                                {/* THE ROW IS THE LINK. A signed-in reader opens an
                                    invoice by tapping the invoice — repeating the
                                    numbers underneath as a second list was a whole
                                    block of screen saying nothing new. Anonymous
                                    readers get the same row, inert. */}
                                {step !== 'card' && (
                                <ul className="apay__list">
                                    {invoices.map((inv) => {
                                        const body = (
                                            <>
                                                <span className="apay__rowMain">
                                                    <span className="apay__num">{inv.number}</span>
                                                    {inv.date ? <span className="apay__date">{when(inv.date)}</span> : null}
                                                    {/* Multi-billing clients invoice through
                                                        several legal entities, and which one
                                                        matters to whoever reconciles this. */}
                                                    {data.multi_billing && inv.entity
                                                        ? <span className="apay__entity">{inv.entity}</span>
                                                        : null}
                                                </span>
                                                <span className="apay__amt">AED {money(inv.balance)}</span>
                                            </>
                                        );
                                        const openable = data.authenticated && inv.id;
                                        return (
                                            <li key={inv.number || inv.id} className="apay__row">
                                                {openable ? (
                                                    <a
                                                        className="apay__rowLink"
                                                        href={`/api/account-link/${token}/invoice/${inv.id}/document`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={`Open ${inv.number}`}
                                                    >
                                                        {body}
                                                    </a>
                                                ) : body}
                                            </li>
                                        );
                                    })}
                                </ul>
                                )}

                                {notice ? <div className="apay__notice">{notice}</div> : null}
                                {error ? <div className="pubpay__error">{error}</div> : null}

                                {/* Nothing on the account → no box at all: an
                                    empty 'signed in' panel is dead space that
                                    only confuses a first-time payer (owner,
                                    2026-09-02). */}
                                {authed && step !== 'card' && total > 0.009 && (walletCeiling > 0.005 || savedCards.length > 0) && (
                                    <div className="pubpay__owner">
                                        <div className="pubpay__ownerHead">✓ Signed in — pay with your account</div>

                                        {/* THE ONE CHECKOUT BLOCK — the same
                                            figures-and-toggle panel every other
                                            client checkout renders (owner,
                                            2026-09-02: "check my toggle in
                                            other surfaces, it's much nicer"). */}
                                        {walletCeiling > 0.005 && (
                                            <PayNowPanel
                                                amount={total}
                                                wallet={data.wallet}
                                                spendable={walletCeiling}
                                                useWallet={useWallet}
                                                onUseWallet={setUseWallet}
                                                onTopUp={null}
                                                label="Outstanding invoices"
                                                disabled={walletPaying || savedCardBusy}
                                            />
                                        )}

                                        {!walletCovers && savedCards.length > 0 && (
                                            <SavedCardPicker
                                                cards={savedCards}
                                                value={cardSelection}
                                                onChange={setCardSelection}
                                                allowSaveNew={false}
                                                disabled={walletPaying || savedCardBusy}
                                                cvvValue={savedCardActive ? savedCvv : null}
                                                onCvvChange={savedCardActive ? setSavedCvv : null}
                                                newCardSub="Continue to input card details."
                                            />
                                        )}

                                        {!walletCovers && savedCardActive && (
                                            <SavedCardPay
                                                card={selectedSavedCard}
                                                compact
                                                cvv={savedCvv}
                                                onCvvChange={setSavedCvv}
                                                onBusyChange={setSavedCardBusy}
                                                payLabel={`Pay AED ${money(cardPortion)}`}
                                                disabled={walletPaying}
                                                charge={chargeSavedCard}
                                                onPaid={() => { setPaidHere(true); load(); }}
                                                onFailed={() => { /* the row states its own outcome */ }}
                                            />
                                        )}
                                    </div>
                                )}

                                {/* THE FORM IS BUILT BEFORE IT IS ASKED FOR.
                                    Measured on the live domain (2026-08-29):
                                    with our config and the SDK already loaded,
                                    mountCardInput still takes ~3s to put its
                                    iframes on the page — and that was happening
                                    entirely AFTER the tap, which is what
                                    "Loading secure card form…" was. It is now
                                    mounted, hidden, as soon as the page knows
                                    there is something to pay, so the three
                                    seconds pass while the amount is being read
                                    and the tap reveals a form that is ready.

                                    Hidden by clipping, not by display:none or by
                                    unmounting: the panel must keep a real width
                                    to choose its layout, and it must be the SAME
                                    panel afterwards (a remount would throw the
                                    iframes away and start the three seconds
                                    again). Nothing is ordered or charged by
                                    mounting — the order is created when Pay is
                                    pressed, never before. */}
                                {hostedRail !== 'off' ? (
                                    <div
                                        className={step === 'card' ? '' : 'apay__prewarm'}
                                        aria-hidden={step !== 'card'}
                                    >
                                        <HostedCardPanel
                                            /* Keyed on the CARD LEG so a split (or
                                               total) that moves builds a new form
                                               rather than re-labelling one the SDK
                                               has already been told the old figure
                                               for. */
                                            key={`al-${cardPortion.toFixed(2)}`}
                                            payLabel={`Pay AED ${money(cardPortion)}`}
                                            amountAed={cardPortion}
                                            configUrl={`/api/account-link/${token}/hosted-session/config`}
                                            startPayment={startHostedPayment}
                                            verifyPayment={verifyHostedPayment}
                                            /* WALLET FIRST HERE. This page is
                                               opened from a WhatsApp reminder on
                                               a phone, so Apple Pay is the point
                                               and the card form is the fallback.
                                               Undocumented SDK options — if a
                                               future build ignores them the
                                               ordinary tabbed form comes back,
                                               which is a worse first tap rather
                                               than a broken page. */
                                            preferredPaymentMethod="DIGITAL_PAYMENTS"
                                            paymentMethodOrder={['DIGITAL_PAYMENTS', 'CARD']}
                                            onUnavailable={() => setHostedRail('off')}
                                            onPaid={() => { setPaidHere(true); load(); }}
                                            onFailed={() => { /* the panel states the outcome and re-arms */ }}
                                            actionSlot={paySlot}
                                        >
                                            {authed && (
                                                <label className="hcard-save">
                                                    <input
                                                        type="checkbox"
                                                        checked={saveNewCard}
                                                        onChange={(e) => setSaveNewCard(e.target.checked)}
                                                    />
                                                    <span>Save this card for future payments</span>
                                                </label>
                                            )}
                                        </HostedCardPanel>
                                        {step === 'card' ? (
                                            <div className="apay__actions">
                                                <button
                                                    type="button"
                                                    className="apay__back"
                                                    onClick={() => setStep('choose')}
                                                >
                                                    Back
                                                </button>
                                                <div className="apay__paySlot" ref={setPaySlot} />
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}

                                {step !== 'card' && total <= 0.009 ? null : step !== 'card' ? (
                                    walletCovers ? (
                                        <button
                                            type="button"
                                            className="apay__pay"
                                            onClick={payFromWallet}
                                            disabled={walletPaying}
                                        >
                                            {walletPaying ? 'Paying from wallet…' : `Pay AED ${money(total)} from wallet`}
                                        </button>
                                    ) : savedCardActive ? null : (
                                        <button
                                            type="button"
                                            className="apay__pay"
                                            onClick={() => (hostedRail !== 'off' ? setStep('card') : pay())}
                                            disabled={paying || walletPaying || savedCardBusy}
                                        >
                                            {paying ? 'Opening secure payment…' : `Pay AED ${money(cardPortion)}`}
                                        </button>
                                    )
                                ) : null}
                            </>
                        )}

                        {/* One quiet line, whatever the state: the statement for
                            a signed-in reader, the way in for everybody else.
                            The invoices themselves are the rows above. */}
                        {data.authenticated ? (
                            /* ONE STATEMENT PER BILLING ACCOUNT. Zoho statements
                               are per contact, so a client who invoices through
                               several legal entities genuinely has several — one
                               combined statement does not exist. Paying is the
                               opposite: the total spans every entity and one tap
                               clears the lot. */
                            <p className="apay__under">
                                {(data.statements || [{ id: 'default', label: '' }]).map((st, i) => (
                                    <React.Fragment key={st.id}>
                                        {i > 0 ? <span className="apay__sep"> · </span> : null}
                                        <a
                                            href={`/api/account-link/${token}/statement?entity=${encodeURIComponent(st.id)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {(data.statements || []).length > 1
                                                ? `${st.label} statement`
                                                : 'View full statement'}
                                        </a>
                                    </React.Fragment>
                                ))}
                            </p>
                        ) : !settled ? (
                            <p className="apay__under">
                                <a href={`/login?next=${encodeURIComponent(window.location.pathname)}`}>
                                    Sign in
                                </a> for invoices and statements or to use your wallet balance and saved card.
                            </p>
                        ) : null}
                    </>
                )}
            </div>

            {/* The payer may have nothing but this link — no account, no app, no
                email thread. Same contact details as the invoice pay page. */}
            <div className="pubpay__pageFoot">
                <div className="pubpay__contact">
                    <a
                        className="pubpay__contactLink"
                        href="https://wa.me/971585891289"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        WhatsApp +971 58 589 1289
                    </a>
                    <a className="pubpay__contactLink" href="mailto:hello@holidayhomecleaning.ae">
                        hello@holidayhomecleaning.ae
                    </a>
                    <a
                        className="pubpay__contactLink"
                        href="https://holidayhomecleaning.ae"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        holidayhomecleaning.ae
                    </a>
                </div>
                <div>Holiday Home Cleaning Services Co. L.L.C · Dubai, UAE</div>
            </div>
        </div>
    );
}
